/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The note form ("band") as the reference strip opens it — one loader and one open/close state
 * for the capture surfaces (the category band, the guide's capture block, the community form page).
 *
 * The three-step form is never in the page's bundle: its module loads on the first answer to the
 * strip's question, warmed on idle once a product is declared so the open has no "Loading…"
 * beat. Held here rather than behind `next/dynamic` so the open is one commit: the module is in
 * hand before the state flips. (The PDP Owners section keeps `next/dynamic`: its band sits in a
 * section that is already on the page.)
 */
import { useCallback, useEffect, useState } from 'react'
import { preloadQuestionSet } from 'lib/owner-notes/questions'

type OwnerNoteBandModule = typeof import('./OwnerNoteBand')
let ownerNoteBandModule: OwnerNoteBandModule | null = null
const loadOwnerNoteBand = (): Promise<OwnerNoteBandModule> =>
  import('./OwnerNoteBand').then(mod => {
    ownerNoteBandModule = mod
    return mod
  })

/** Run after the page settles — the preload must never compete with the page's own work. */
function whenIdle(fn: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(fn, { timeout: 3000 })
    return () => window.cancelIdleCallback(id)
  }
  const id = window.setTimeout(fn, 1500)
  return () => window.clearTimeout(id)
}

export interface NoteBandState {
  bandOpen: boolean
  /** Opened from the reader's own card: the band edits the existing note. */
  editing: boolean
  /** The strip's ask is the buy-again question: the tapped answer rides into the band. */
  seedBuyAgain: boolean | undefined
  open: (edit: boolean, buyAgain?: boolean) => void
  close: () => void
  /** Never null while `bandOpen`: every open awaits the module. */
  OwnerNoteBand: OwnerNoteBandModule['default'] | null
}

/** `preloadNodeId`: the declared product once an ask can be made (else null = warm nothing). */
export function useNoteBand(preloadNodeId: string | null): NoteBandState {
  const [bandOpen, setBandOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [seedBuyAgain, setSeedBuyAgain] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    if (!preloadNodeId) {
      return undefined
    }
    return whenIdle(() => {
      loadOwnerNoteBand().catch(() => undefined)
      preloadQuestionSet(preloadNodeId)
    })
  }, [preloadNodeId])

  const open = useCallback((edit: boolean, buyAgain?: boolean): void => {
    const reveal = (): void => {
      // The chunk did not arrive (offline, a deploy mid-session): nothing opens rather than a
      // strip that stopped asking — the question stays up and the next answer tries again.
      if (!ownerNoteBandModule) {
        return
      }
      setSeedBuyAgain(buyAgain)
      setEditing(edit)
      setBandOpen(true)
    }
    loadOwnerNoteBand().then(reveal, reveal)
  }, [])
  const close = useCallback((): void => setBandOpen(false), [])

  return {
    bandOpen,
    editing,
    seedBuyAgain,
    open,
    close,
    OwnerNoteBand: ownerNoteBandModule?.default ?? null,
  }
}
